// The closing scene for every page, shown right before the footer.
// type: media (photo or clip that opens to full width), count (a big number), stack (photos that fan out),
// logos (partner logos that settle into a row), flags (Aurora countries), line (a statement that fills in as you scroll).
import { AAROH_ALBUM, AAROH_CLIPS } from "./album.js";
import { siteRoutes } from "./routes.js";
import { coreTeamGroups } from "./content.js";

const photo = (id) => {
  const item = AAROH_ALBUM.find((entry) => entry.id === id);
  return item ? { src: item.srcSmall ?? item.src, large: item.src, alt: item.alt } : null;
};
const clip = (id) => {
  const item = AAROH_CLIPS.find((entry) => entry.id === id);
  return item ? { video: item.src, poster: item.poster, alt: item.alt } : null;
};
const still = (src, alt) => ({ src, large: src, alt });

const TEAM_SIZE = coreTeamGroups.reduce((sum, group) => sum + group.members.length, 0);
const MAIL = "mailto:founder@projectgrid.org";

export const PARTNER_LOGOS = [
  ["Tachyon India", "/images/partners/tachyon-india.png"],
  ["CiPD, IIIT Delhi", "/images/partners/cipd-iiitd.png"],
  ["GradCircle", "/images/partners/supporters/gradcircle.webp"],
  ["Stacks KB", "/images/partners/supporters/stacks.png"],
  ["Fotocart", "/images/partners/supporters/fotocart.png"],
  [".xyz", "/images/partners/supporters/xyz.png"],
  ["GitHub", "/images/partners/supporters/github.png"],
  ["Skills4U", "/images/partners/supporters/skills4u.webp"],
  ["HP", "/images/partners/supporters/hp.png"],
  ["Tale of Humankind Foundation", "/images/partners/tale-of-humankind.webp"],
  ["IIT Delhi, Office of Academic Outreach", "/images/partners/iit-delhi-aoni.png"],
];

const METAMORPHOSIS_GROUP = still("/images/metamorphosis/album/meta-745-cohort-group-photo-1600.webp", "Metamorphosis students packing a lecture hall for a group photo at IIT Delhi");
const METAMORPHOSIS_CLOSE = still("/images/metamorphosis/album/meta-538-changemaker-certificates-stage-1600.webp", "Metamorphosis students holding up their certificates on stage at IIT Delhi");
const FOUNDERS = still("/images/grid-photo-06-p7.jpg", "GRID founders Moksh Sindhwani and Tanishq Aggarwal during Metamorphosis");
const TEAM_PHOTO = still("/images/grid-photo-09-p8.jpg", "GRID founders together");
const AURORA_WINNER = still("/images/grid-photo-10-p8.jpg", "Aurora overall winner Jerovin Floyd holding his HP prize laptop");
const AURORA_BUILDER = still("/images/grid-photo-11-p8.jpg", "An Aurora Top 20 builder");

export const ENDINGS = {
  "/": {
    type: "media",
    line: "Bring an idea. We will help you build it.",
    media: clip("M028"),
    caption: "The main hall at AAROH during build hours.",
    action: ["Join GRID", "/get-involved"],
    next: "/about",
  },
  "/about": {
    type: "media",
    line: "Two students started this in December 2025.",
    media: FOUNDERS,
    caption: "Moksh and Tanishq during Metamorphosis.",
    next: "/journey",
  },
  "/journey": {
    type: "stack",
    line: "Under a year in, three programmes done.",
    photos: [AURORA_WINNER, photo("M054"), METAMORPHOSIS_GROUP, photo("M609"), photo("M262")],
    next: "/impact",
  },
  "/impact": {
    type: "count",
    value: "2,013",
    label: "participants across AAROH, Aurora and Metamorphosis",
    line: "Help us add the next number.",
    photos: [photo("M054"), AURORA_WINNER, METAMORPHOSIS_GROUP],
    action: ["Join GRID", "/get-involved"],
    next: "/team",
  },
  "/team": {
    type: "count",
    value: String(TEAM_SIZE),
    label: "students run GRID today",
    line: "There is room on this team.",
    photos: [TEAM_PHOTO, FOUNDERS, photo("M465")],
    action: ["Join the team", "/student-opportunities/team"],
    next: "/partnerships",
  },
  "/partnerships": {
    type: "logos",
    line: "Eleven so far. Bring a problem worth solving.",
    action: ["Email a proposal", `${MAIL}?subject=Partnership%20proposal`],
    next: "/programmes",
  },
  "/governance": {
    type: "line",
    line: "Questions about how GRID is run? Write to founder@projectgrid.org.",
    action: ["Email the founders", MAIL],
    next: "/privacy",
  },
  "/media": {
    type: "stack",
    line: "Every photo keeps its caption.",
    photos: [photo("M262"), photo("M533"), photo("M609"), photo("M083"), photo("M210")],
    next: "/stories",
  },
  "/meetings": {
    type: "stack",
    line: "Five conversations, each written down.",
    fromRoutes: "/meetings/",
    next: "/partnerships",
  },
  "/programmes": {
    type: "count",
    value: "3",
    label: "programmes so far: AAROH, Aurora and Metamorphosis",
    line: "Be in the room next time.",
    photos: [photo("M054"), AURORA_WINNER, METAMORPHOSIS_GROUP],
    action: ["Upcoming events", "/programmes/upcoming"],
    next: "/programmes/aaroh",
  },
  "/programmes/aaroh": {
    type: "media",
    line: "363 students. Two days. One hall.",
    media: clip("M029"),
    caption: "A team argues out an idea at the laptop.",
    next: "/programmes/aurora",
  },
  "/programmes/aurora": {
    type: "flags",
    line: "Students joined from 27+ countries.",
    next: "/programmes/metamorphosis",
  },
  "/programmes/metamorphosis": {
    type: "media",
    line: "150 students from 18 states, one week at IIT Delhi.",
    media: METAMORPHOSIS_CLOSE,
    caption: "Certificates on stage. GRID was the outreach partner.",
    next: "/programmes/upcoming",
  },
  "/programmes/upcoming": {
    type: "media",
    line: "Hear about the next one first.",
    media: photo("M010"),
    caption: "The AAROH hall before the doors opened.",
    action: ["Contact us", "/contact"],
    next: "/for-students",
  },
  "/student-opportunities": {
    type: "line",
    line: "We only list opportunities we can check.",
    next: "/student-opportunities/team",
  },
  "/student-opportunities/organisations": {
    type: "media",
    line: "Send a short brief. We reply by email.",
    media: photo("M631"),
    caption: "A team and guests with the trophy at AAROH.",
    next: "/for-partners",
  },
  "/student-opportunities/team": {
    type: "count",
    value: String(TEAM_SIZE),
    label: "students on the team today",
    line: "Run a real part of GRID.",
    photos: [TEAM_PHOTO, photo("M465"), photo("M580")],
    next: "/student-opportunities/internships",
  },
  "/student-opportunities/internships": {
    type: "media",
    line: "A scoped project with a clear output.",
    media: photo("M580"),
    caption: "Checking in on a team's progress at AAROH.",
    next: "/contact",
  },
  "/problems": {
    type: "stack",
    line: "Pick a problem. Then write it down properly.",
    photos: [photo("M102"), photo("M189"), photo("M104"), photo("M191"), photo("M438")],
    next: "/problems/education",
  },
  "/projects": {
    type: "media",
    line: "Built something? Share it with GRID.",
    media: AURORA_BUILDER,
    caption: "An Aurora Top 20 builder.",
    action: ["Share your project", `${MAIL}?subject=Student%20project`],
    next: "/guides",
  },
  "/guides": {
    type: "stack",
    line: "Five guides. Free, and short.",
    photos: [photo("M102"), photo("M189"), photo("M438"), photo("M161"), photo("M360")],
    next: "/guides/find-a-real-world-problem",
  },
  "/stories": {
    type: "stack",
    line: "More notes as more happens.",
    photos: [photo("M533"), photo("M210"), photo("M241"), photo("M689"), photo("M211")],
    next: "/stories/what-is-project-grid",
  },
  "/for-students": {
    type: "media",
    line: "Three people, one laptop. Start there.",
    media: clip("M069"),
    caption: "A quick discussion mid-build at AAROH.",
    action: ["Find a guide", "/guides"],
    next: "/guides",
  },
  "/for-schools": {
    type: "count",
    value: "363",
    label: "young innovators at AAROH, IIIT Delhi",
    line: "Bring GRID to your school.",
    photos: [photo("M054"), photo("M682"), photo("M609")],
    action: ["Contact us", "/contact"],
    next: "/programmes/aaroh",
  },
  "/for-partners": {
    type: "logos",
    line: "Start with one clear note.",
    action: ["Email a proposal", `${MAIL}?subject=Partnership%20proposal`],
    next: "/partnerships",
  },
  "/get-involved": {
    type: "stack",
    line: "Pick one way in. You can change later.",
    photos: [photo("M533"), photo("M110"), photo("M098"), photo("M689"), photo("M059")],
    next: "/contact",
  },
  "/contact": {
    type: "line",
    line: "We read every note that comes in.",
    action: ["Email founder@projectgrid.org", MAIL],
    next: "/",
  },
  "/privacy": {
    type: "line",
    line: "Questions about your information? Write to founder@projectgrid.org.",
    next: "/terms",
  },
  "/terms": {
    type: "line",
    line: "Questions about these terms? Write to us before you reuse anything.",
    next: "/governance",
  },
};

// Families of detail pages share one scene and hand off to the next page in the same family.
const FAMILIES = [
  { prefix: "/meetings/", hub: "/meetings", type: "media", line: "A meeting is not an endorsement." },
  { prefix: "/problems/", hub: "/guides", type: "media", line: "Start small, and ask the people it affects." },
  { prefix: "/guides/", hub: "/projects", type: "media", line: "Now try it on a real problem." },
  { prefix: "/stories/", hub: "/media", type: "media", line: "Seen something we got wrong? Tell us." },
];

export function endingFor(pathname) {
  if (ENDINGS[pathname]) {
    const ending = ENDINGS[pathname];
    if (ending.fromRoutes) {
      const photos = siteRoutes
        .filter((item) => item.path.startsWith(ending.fromRoutes))
        .map((item) => still(item.heroMedia, `Photo: ${item.title}`));
      return { ...ending, photos };
    }
    return ending;
  }
  const family = FAMILIES.find((item) => pathname.startsWith(item.prefix));
  if (!family) return null;
  const members = siteRoutes.filter((item) => item.path.startsWith(family.prefix));
  const index = members.findIndex((item) => item.path === pathname);
  if (index < 0) return null;
  const current = members[index];
  return {
    type: family.type,
    line: family.line,
    media: still(current.heroMedia, `Photo from ${current.title}`),
    next: members[index + 1]?.path ?? family.hub,
  };
}

export function endingThumb(pathname) {
  const ending = endingFor(pathname);
  if (!ending) return siteRoutes.find((item) => item.path === pathname)?.heroMedia ?? null;
  if (ending.media) return ending.media.poster ?? ending.media.src;
  if (ending.photos?.length) return ending.photos[0]?.src;
  return siteRoutes.find((item) => item.path === pathname)?.heroMedia ?? null;
}
